import React, { useState, useEffect } from 'react';

interface AnimatedCodePreviewProps {
  isDarkMode: boolean;
  currentStep: string;
  progress: number;
  projectType?: string;
}

const buildingStatuses = [
  '🎨 Designing the interface',
  '⚙️ Setting up components',
  '📝 Writing clean code',
  '🎯 Creating index file',
  '💎 Adding premium features',
  '🚀 Optimizing performance',
  '✨ Polishing the design',
  '🔧 Configuring settings',
  '📦 Building modules',
  '🎭 Adding animations',
  '🌈 Applying color schemes',
  '📱 Making it responsive',
  '⚡ Enhancing speed',
  '🎪 Testing interactions',
  '🏗️ Building frontend',
  '🔨 Crafting backend',
  '💅 Styling components',
  '🎨 Building the interface'
];

export const AnimatedCodePreview: React.FC<AnimatedCodePreviewProps> = ({
  isDarkMode,
  currentStep,
  progress,
  projectType
}) => {
  const [currentStatus, setCurrentStatus] = useState(buildingStatuses[0]);
  const [statusIndex, setStatusIndex] = useState(0);

  // Cycle through status messages - each replaces the previous one
  useEffect(() => {
    if (progress === 0) {
      setCurrentStatus('Codey is waiting for your idea...');
      return;
    }

    const interval = setInterval(() => {
      setStatusIndex(prev => {
        const next = (prev + 1) % buildingStatuses.length;
        setCurrentStatus(buildingStatuses[next]);
        return next;
      });
    }, 2000); // Change every 2 seconds

    return () => clearInterval(interval);
  }, [progress]);

  // Simulate early preview - show wireframe based on progress
  const showWireframe = progress > 10 && progress < 100;
  const wireframeOpacity = Math.min(progress / 30, 1); // Fade in as progress increases

  return (
    <div className={`h-full flex flex-col relative overflow-hidden ${
      isDarkMode
        ? 'bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950'
        : 'bg-gradient-to-br from-gray-50 via-white to-gray-50'
    }`}>

      {/* MAIN CONTENT AREA */}
      <div className="flex-1 flex flex-col items-center justify-center px-6 pt-8">

        {/* CODEY STATUS BADGE */}
        <div className={`mb-6 px-6 py-4 rounded-2xl backdrop-blur-xl border shadow-lg ${
          isDarkMode
            ? 'bg-gradient-to-r from-blue-500/10 via-indigo-500/10 to-purple-500/10 border-white/5'
            : 'bg-gradient-to-r from-blue-50/70 via-indigo-50/70 to-purple-50/70 border-gray-200/30'
        }`}>
          <div className="flex items-center gap-4">
            <div className={`w-12 h-12 rounded-xl flex items-center justify-center shadow-lg ${
              isDarkMode
                ? 'bg-gradient-to-br from-blue-500 via-indigo-500 to-purple-500 shadow-blue-500/20'
                : 'bg-gradient-to-br from-blue-400 via-indigo-400 to-purple-400 shadow-blue-400/15'
            }`}>
              <span className="text-2xl animate-bounce">🤖</span>
            </div>
            <div>
              <h3 className={`text-xl font-semibold tracking-tight ${
                isDarkMode ? 'text-white' : 'text-gray-900'
              }`}>
                {progress === 0 ? 'Codey is waiting for your idea' : 'Codey is building'}
              </h3>
              <p className={`text-sm font-medium mt-1 ${
                isDarkMode ? 'text-blue-200' : 'text-blue-600'
              }`}>
                {progress === 0 ? 'Type your idea below to get started!' : (currentStep || 'Generating your project...')}
              </p>
            </div>
          </div>
        </div>

        {/* SINGLE STATUS - Replaces previous with fade animation */}
        <div className="flex flex-col items-center justify-center px-4 mb-6 min-h-[32px]">
          <div
            key={statusIndex}
            className={`flex items-center gap-2 px-3 py-1 font-medium text-xs transition-all duration-300 animate-fade-in ${
              isDarkMode
                ? 'text-blue-200'
                : 'text-blue-700'
            }`}
          >
            <span className="text-sm animate-pulse">{currentStatus.split(' ')[0]}</span>
            <span className="font-medium">{currentStatus.substring(currentStatus.indexOf(' ') + 1)}</span>
            <div className={`w-1.5 h-1.5 rounded-full animate-ping ${
              isDarkMode ? 'bg-blue-400' : 'bg-blue-500'
            }`}></div>
          </div>
        </div>

        {/* EARLY PREVIEW WIREFRAME */}
        {showWireframe && (
          <div
            className={`w-full max-w-4xl rounded-2xl overflow-hidden shadow-2xl border transition-opacity duration-1000 mb-8 ${
              isDarkMode
                ? 'bg-slate-800/50 border-white/10 backdrop-blur-xl'
                : 'bg-white/80 border-gray-200/50 backdrop-blur-xl'
            }`}
            style={{ opacity: wireframeOpacity }}
          >
            {/* Preview Header */}
            <div className={`h-12 flex items-center justify-between px-6 border-b ${
              isDarkMode ? 'bg-slate-900/80 border-white/5' : 'bg-gray-50/80 border-gray-200/30'
            }`}>
              <div className="flex items-center gap-2">
                <div className={`w-3 h-3 rounded-full ${isDarkMode ? 'bg-red-400' : 'bg-red-500'}`}></div>
                <div className={`w-3 h-3 rounded-full ${isDarkMode ? 'bg-yellow-400' : 'bg-yellow-500'}`}></div>
                <div className={`w-3 h-3 rounded-full ${isDarkMode ? 'bg-green-400' : 'bg-green-500'}`}></div>
              </div>
              <div className={`text-xs font-mono ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                localhost:3000
              </div>
            </div>

            {/* Wireframe Content */}
            <div className={`p-8 ${isDarkMode ? 'bg-slate-900/50' : 'bg-gray-50/50'}`}>
              <div className={`space-y-6 animate-pulse`}>
                {/* Header skeleton */}
                <div className={`h-16 rounded-lg ${isDarkMode ? 'bg-slate-700/30' : 'bg-gray-200/50'}`}></div>

                {/* Content skeletons */}
                <div className="grid grid-cols-3 gap-4">
                  <div className={`h-32 rounded-lg ${isDarkMode ? 'bg-slate-700/30' : 'bg-gray-200/50'}`}></div>
                  <div className={`h-32 rounded-lg ${isDarkMode ? 'bg-slate-700/30' : 'bg-gray-200/50'}`}></div>
                  <div className={`h-32 rounded-lg ${isDarkMode ? 'bg-slate-700/30' : 'bg-gray-200/50'}`}></div>
                </div>

                <div className={`h-48 rounded-lg ${isDarkMode ? 'bg-slate-700/30' : 'bg-gray-200/50'}`}></div>
              </div>

              {/* Status overlay */}
              <div className={`mt-8 text-center text-sm font-bold ${
                isDarkMode ? 'text-blue-400' : 'text-blue-600'
              }`}>
                ✨ Building your {projectType || 'project'}... Features and design are being added!
                <br />
                <span className={`text-xs ${isDarkMode ? 'text-gray-500' : 'text-gray-500'}`}>
                  Live preview will be ready in a moment
                </span>
              </div>
            </div>
          </div>
        )}

        {/* PROGRESS CIRCLE */}
        <div className="relative w-32 h-32">
          <svg className="w-full h-full transform -rotate-90">
            {/* Background circle */}
            <circle
              cx="64"
              cy="64"
              r="56"
              stroke={isDarkMode ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.08)'}
              strokeWidth="4"
              fill="none"
            />
            {/* Progress circle */}
            <circle
              cx="64"
              cy="64"
              r="56"
              stroke="url(#gradient)"
              strokeWidth="4"
              fill="none"
              strokeDasharray={`${2 * Math.PI * 56}`}
              strokeDashoffset={`${2 * Math.PI * 56 * (1 - progress / 100)}`}
              strokeLinecap="round"
              className="transition-all duration-700 ease-out"
              style={{
                filter: 'drop-shadow(0 0 6px rgba(59, 130, 246, 0.2))'
              }}
            />
            <defs>
              <linearGradient id="gradient" x1="0%" y1="0%" x2="100%" y2="100%">
                <stop offset="0%" stopColor="#3B82F6" />
                <stop offset="30%" stopColor="#8B5CF6" />
                <stop offset="70%" stopColor="#EC4899" />
                <stop offset="100%" stopColor="#F59E0B" />
              </linearGradient>
            </defs>
          </svg>
          {/* Center percentage */}
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="text-center">
              <div className={`text-3xl font-semibold ${
                isDarkMode ? 'text-white' : 'text-gray-900'
              }`}>
                {Math.round(progress)}%
              </div>
              <div className={`text-xs font-medium mt-1 ${
                isDarkMode ? 'text-gray-300' : 'text-gray-600'
              }`}>
                Complete
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* BOTTOM GRADIENT GLOW */}
      <div className={`absolute bottom-0 left-0 right-0 h-32 pointer-events-none ${
        isDarkMode
          ? 'bg-gradient-to-t from-blue-500/10 via-transparent to-transparent'
          : 'bg-gradient-to-t from-blue-200/20 via-transparent to-transparent'
      }`}></div>
    </div>
  );
};
